// src/pages/CollectDepositPage.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import Button from "../components/Button";
import PageHeader from "../components/PageHeader";
import { getDepositDetails, collectDeposit } from "../services/depositService";
import "./CollectDepositPage.css";
import { checkIdentityStatus } from "../services/identityService";

function CollectDepositPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const depositId = queryParams.get("depositId");
  const token = queryParams.get("token");

  const [depositDetails, setDepositDetails] = useState(null);
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // New state for identity check:
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationUrl, setVerificationUrl] = useState("");
  
  // Si depositId ou token n'est pas fourni, on affiche un message d'erreur.
  if (!depositId || !token) {
    return (
      <div className="collect-container">
        <PageHeader
          title={t("collectDeposit.pageErrorTitle")}
          subtitle= {t("collectDeposit.pageErrorSubtitle")}
        />
      </div>
    );
  }
  
  useEffect(() => {
    // Load deposit details
    getDepositDetails(depositId)
      .then((data) => {
        setDepositDetails(data);
      })
      .catch((err) => {
        setError(`${t("collectDeposit.error")} ${err.message}`);
      });
    
    // Check if the user needs to verify their identity
    checkIdentityStatus(depositId)
      .then((result) => {
        if (!result.isVerified) {
          setNeedsVerification(true);
          setVerificationUrl(result.verificationUrl);
        } else {
          setNeedsVerification(false);
        }
      })
      .catch((err) => {
        console.error("Erreur lors de la vérification de l'identité :", err);
        // Optionnel : vous pouvez décider de ne pas bloquer l'accès en cas d'erreur de vérification
      });
  }, [depositId, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!depositDetails) {
      setError(t("collectDeposit.error") + " " + "Deposit details not loaded.");
      return;
    }

    const totalAmount = parseFloat(depositDetails.depositAmount);
    const collectedAmount = parseFloat(depositDetails.collectedAmount || 0);
    const maxCollectable = totalAmount - collectedAmount;

    const amountValue = parseFloat(amount);

    if (isNaN(amountValue) || amountValue < 0.5) {
      setError(t("collectDeposit.error") + " Minimum 0.5€");
      return;
    }

    if (amountValue > maxCollectable) {
      setError(t("collectDeposit.error") + ` Maximum ${maxCollectable.toFixed(2)}€`);
      return;
    }

    setLoading(true);
    try {
      await collectDeposit(depositId, token, amountValue);
      setMessage(t("collectDeposit.success"));
      setTimeout(() => navigate("/"), 3000);
    } catch (err) {
      setError(`${t("collectDeposit.error")} ${err.message}`);
      setLoading(false);
    }
  };
  
  // If the user needs identity verification, show a button to verify their identity
  if (needsVerification) {
    return (
      <div className="collect-container">
        <PageHeader
          title={t("collectDeposit.verificationTitle")} 
          subtitle={t("collectDeposit.verificationSubtitle")} 
        />
        <Button
          label={t("collectDeposit.verificationButton")} 
          type="submit"
          onClick={() => {
            window.location.href = verificationUrl;
          }}
         >   
        </Button>
      </div>
    );
  }

  return (
    <Layout>
      <div className="collect-container">
        <PageHeader
          title={t("collectDeposit.title")}
          subtitle={t("collectDeposit.description")}
        />

        <form onSubmit={handleSubmit}>
          <label htmlFor="amount">{t("collectDeposit.labelField")}</label>
          <input
            type="number"
            step="0.01"
            min="0.5"
            id="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            disabled={loading || !depositDetails || needsVerification}
          />

          <Button
            label={loading ? t("collectDeposit.loading") : t("collectDeposit.submitButton")}
            type="submit"
            disabled={loading || !depositDetails}
            onClick={handleSubmit}
          />

          {error && <p className="error">{error}</p>}
          {message && <p className="message">{message}</p>}
        </form>
      </div>
    </Layout>
  );
}

export default CollectDepositPage;
